import React, { useState, useEffect, useRef } from "react";
import ArrowIcon from '../../images/arrow_button.svg'
import "./DigitalPrivacy.css";

const AccordionSection = ({
  title,
  children,
  isOpen,
  onToggle,
  sectionRef,
}) => {
  return (
    <div
      className={`accordion-section ${isOpen ? "open" : ""}`}
      ref={sectionRef}
    >
      <button
        className="accordion-header"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span>{title}</span>
        <div className="accordion-icon-wrapper">
        <img src={ArrowIcon} alt="Arrow Icon" className={`accordion-icon ${isOpen ? "open" : ""}`}/>
        </div>
      </button>
      {isOpen && <div className="accordion-content">{children}</div>}
    </div>
  );
};

const DigitalPrivacy = () => {
  const [showSkipButton, setShowSkipButton] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  const [openSections, setOpenSections] = useState([]);

  // Refs for each accordion section
  const personalRef = useRef(null);
  const servicesRef = useRef(null);
  const rightsRef = useRef(null);

  const handleLinkClick = (section) => {
    setOpenSections((prev) =>
      prev.includes(section) ? prev : [...prev, section] // keep others open
    );
    setTimeout(() => {
      if (section === "personal" && personalRef.current) {
        personalRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (section === "services" && servicesRef.current) {
        servicesRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (section === "rights" && rightsRef.current) {
        rightsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };
  const configureConsentUI = (shouldShowSkip) => {
    try {
      setShowSkipButton(shouldShowSkip);
      return "Configuration applied successfully";
    } catch (error) {
      console.error("Error applying configuration:", error);
      return "Error applying configuration";
    }
  };

  const toggleSkipButton = (show) => setShowSkipButton(show);

  const notifyPlatform = (eventType) => {
    const payload = { message: `privacy_${eventType}` };
    const message = {
      action: eventType,
      data: {
        consent:
          eventType === "accept"
            ? true
            : eventType === "deny"
            ? false
            : "skipped",
        timestamp: new Date().toISOString(),
      },
      timestamp: new Date().toISOString(),
    };

    try {
      if (window.webkit?.messageHandlers?.nativeApp) {
        window.webkit.messageHandlers.nativeApp.postMessage(payload);
      }
      if (window.webkit?.messageHandlers?.consentHandler) {
        window.webkit.messageHandlers.consentHandler.postMessage(message);
      }
      if (
        eventType === "accept" &&
        window.webkit?.messageHandlers?.acceptClicked
      ) {
        window.webkit.messageHandlers.acceptClicked.postMessage(null);
      }
      if (eventType === "deny" && window.webkit?.messageHandlers?.denyClicked) {
        window.webkit.messageHandlers.denyClicked.postMessage(message);
      }
      if (eventType === "skip" && window.webkit?.messageHandlers?.skipClicked) {
        window.webkit.messageHandlers.skipClicked.postMessage(message);
      }

      if (typeof window.AndroidInterface !== "undefined") {
        if (eventType === "accept") window.AndroidInterface.acceptClicked();
        else if (eventType === "deny") window.AndroidInterface.denyClicked();
        else if (eventType === "skip") window.AndroidInterface.skipClicked();
      }

      if (
        typeof window.Android !== "undefined" &&
        window.Android.onConsentAction
      ) {
        window.Android.onConsentAction(JSON.stringify(message));
      } else if (typeof window.postMessage === "function") {
        window.postMessage(payload, "*");
      }

      if (window.parent && window.parent !== window) {
        window.parent.postMessage(message, "*");
      }
    } catch (error) {
      console.error(`Error during ${eventType} click`, error);
      alert(`Privacy policy ${eventType}`);
    }

    console.log("Privacy Consent Action:", message);
  };

  const handleAccept = () => {
    setShowLoader(true);
    notifyPlatform("accept");
  };
  const handleDeny = () => {
    setShowLoader(true);
    notifyPlatform("deny");
  };
  const handleSkip = () => {
    setShowLoader(true);
    notifyPlatform("skip");
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.hideConsentLoader = () => setShowLoader(false);
      window.showConsentLoader = () => setShowLoader(true);
      window.configureConsentUI = configureConsentUI;
      window.toggleSkipButton = toggleSkipButton;
    }
    return () => {
      if (typeof window !== "undefined") {
        delete window.hideConsentLoader;
        delete window.showConsentLoader;
        delete window.configureConsentUI;
        delete window.toggleSkipButton;
      }
    };
  }, []);

  const toggleSection = (section) => {
    setOpenSections((prev) =>
      prev.includes(section)
        ? prev.filter((s) => s !== section) // close if already open
        : [...prev, section] // open in addition
    );
  };

  return (
    <div className="privacy-container">
      <div className="privacy-modal-container">
        <h1 className="privacy-heading">
          We Value Your Privacy – Please Review & Accept
        </h1>
        <p className="privacy-desc">
          At Chalo, we need to collect your{" "}
          <span
            className="inline-link"
            onClick={() => handleLinkClick("personal")}
          >
            personal data
          </span>{" "}
          to provide{" "}
          <span
            className="inline-link"
            onClick={() => handleLinkClick("services")}
          >
            our services
          </span>
          , and you have the{" "}
          <span
            className="inline-link"
            onClick={() => handleLinkClick("rights")}
          >
            right
          </span>{" "}
          to manage it.
          <br />
          <br />
          Your privacy matters to us, so please take a moment to check our
          updated{" "}
          <a
            className="hyperlink-text"
            href="https://chalo.com/terms-of-use#privacypolicy"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
          . Without consent, access to the app will not be possible.
        </p>

        <div className="accordion-group">
          <AccordionSection
            title="Personal data examples"
            sectionRef={personalRef}
            isOpen={openSections.includes("personal")}
            onToggle={() => toggleSection("personal")}
          >
            <div className="privacy-info-box">
              <ul>
                <li>
                  <span className="privacy-info-title">Personal info:</span>{" "}
                  <span className="privacy-info-desc">
                    Name, phone number, gender, age, Aadhar/ student id/
                    disability certificate (as applicable).
                  </span>
                </li>
                <li>
                  <span className="privacy-info-title">App & travel:</span>{" "}
                  <span className="privacy-info-desc">
                    Pass ID, Chalo Card, routes, stops, transaction and search
                    history.
                  </span>
                </li>
                <li>
                  <span className="privacy-info-title">Financial:</span>{" "}
                  <span className="privacy-info-desc">
                    Payment details, wallet balance, ID, electricity info.
                  </span>
                </li>
                <li>
                  <span className="privacy-info-title">Location & device:</span>{" "}
                  <span className="privacy-info-desc">
                    Coordinates, device ID, carrier, WiFi, screen size, app
                    version.
                  </span>
                </li>
                <li>
                  <span className="privacy-info-title">System:</span>{" "}
                  <span className="privacy-info-desc">
                    Language preference, feature usage and performance
                    analytics.
                  </span>
                </li>
              </ul>
            </div>
          </AccordionSection>

          <AccordionSection
            title="Our services"
            sectionRef={servicesRef}
            isOpen={openSections.includes("services")}
            onToggle={() => toggleSection("services")}
          >
            <p className="privacy-desc">
              Ticketing, live tracking, enhanced user experience, Chalo/ third
              party product/ services’ promotions, security, compliance,
              research and analytics.
            </p>
          </AccordionSection>

          <AccordionSection
            title="Your rights"
            sectionRef={rightsRef}
            isOpen={openSections.includes("rights")}
            onToggle={() => toggleSection("rights")}
          >
            <p className="privacy-desc">
              You can access, update, correct or delete your data, or withdraw
              consent any time. For assistance, contact us at contact@chalo.com
            </p>
          </AccordionSection>
        </div>

        <div className="privacy-btn-group">
          <div className="privacy-consent">
            I am above 18 years, and have read, understood, and consent to
            Chalo’s privacy policy. / My child is below 18 years, and I as the
            parent have read, understood, and consented to Chalo’s privacy
            policy.
          </div>
          <button className="privacy-btn accept" onClick={handleAccept}>
            Accept
          </button>
          <button className="privacy-btn deny" onClick={handleDeny}>
            Deny
          </button>
          <button
            className={`privacy-btn skip ${showSkipButton ? "" : "hidden"}`}
            onClick={handleSkip}
          >
            I will do this later
          </button>
        </div>
      </div>

      {showLoader && (
        <div className="fullscreen-loader">
          <div className="loader-container">
            <div className="loader-spinner"></div>
            <div className="loader-text">Loading...</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DigitalPrivacy;
